/**
 * IS THE SHOPPER IN CART MODE — the question the header's "Show cart" switch turns on.
 *
 * Founder rule (16/09, widened 05/10/2026): the switch is hers only while NO buy-box in the store puts the
 * shopper in a cart — each one is the cash-on-delivery order form or « Acheter maintenant », neither of which
 * ever writes the cart (buy-now hands its lines straight to the checkout since 28/09). **Everything else is
 * greyed and ON**, and "everything else" includes every case we cannot answer.
 *
 * The answer is STORE-WIDE. The header is ONE document per store (chrome went global on 16/08), so a rule that
 * read the page open in the editor got it wrong both ways — it freed the switch from a page that proved nothing
 * (the home page), and it kept it locked on a « Buy now » product page because "the store takes cards", when no
 * page added to cart. So the store answers: the backend lists the buy-boxes of every OTHER draft page
 * (`GET /api/builder/buy-box-journeys`, loaded with each page open — `buyBoxJourneysElsewhere`), the page open
 * here contributes its own buy-boxes LIVE (so flipping a buy-box to « Buy now » unlocks the switch at once),
 * and `shopperCanHaveCart` resolves each stored journey through the store's facts with the very function the
 * buy-box uses. The storefront header runs the same function over the published pages, so the editor and the
 * live store can never disagree about the icon.
 *
 * Fail-closed, like every cut before it: an unknown payment method, or other pages not (yet) answered, locks.
 */
import { useMemo } from 'react';
import { type BuyBoxJourney, type SectionInstance, shopperCanHaveCart } from '@aquilixshop/ui-react';
import { useBuilderStore } from './builderStore';

/** The stored journeys of the buy-boxes PAINTED on this page — a disabled section or block is not one. */
export function openPageOrderActions(sections: readonly SectionInstance[]): string[] {
  const out: string[] = [];
  for (const section of sections) {
    if (section.disabled) continue;
    for (const block of section.blocks ?? []) {
      if (block.type !== 'cod-form' || block.disabled) continue;
      const raw = block.settings['orderAction'];
      out.push(typeof raw === 'string' ? raw : 'auto');
    }
  }
  return out;
}

/**
 * The store's buy-boxes: the other pages (as the backend listed them) + this page (live). `null` while the
 * other pages are not known — not loaded yet, or the fetch failed — which the rule reads as "locked".
 */
export function storeBuyBoxJourneys(
  elsewhere: readonly BuyBoxJourney[] | null,
  openPage: { readonly template: string; readonly handle: string },
  sections: readonly SectionInstance[],
): readonly BuyBoxJourney[] | null {
  if (elsewhere === null) return null;
  return [
    ...elsewhere,
    ...openPageOrderActions(sections).map((orderAction) => ({ ...openPage, orderAction })),
  ];
}

/** The rule itself, fail-closed: unknown method or unknown other pages → cart mode (locked, icon ON). */
export function storeIsInCartMode(
  journeys: readonly BuyBoxJourney[] | null,
  paymentMethod: 'CARD' | 'COD' | null,
  codEnabled: boolean,
): boolean {
  if (paymentMethod === null || journeys === null) return true;
  return shopperCanHaveCart(journeys, codEnabled, paymentMethod === 'CARD' ? 'cart' : 'funnel');
}

/**
 * The store-wide list, for a component. The open page's part is read through a selector that returns a
 * STRING (its actions, serialised), so a per-section canvas cell subscribing here re-renders when a
 * buy-box's journey changes and not on every edit of every section — `MemoSection` is memoized for
 * exactly that. JSON rather than a joined string: a stored journey is whatever the document holds.
 */
export function useStoreBuyBoxJourneys(): readonly BuyBoxJourney[] | null {
  const elsewhere = useBuilderStore((s) => s.buyBoxJourneysElsewhere);
  const template = useBuilderStore((s) => s.route.template);
  const handle = useBuilderStore((s) => s.route.handle);
  const openKey = useBuilderStore((s) =>
    JSON.stringify(openPageOrderActions(Object.values(s.sectionsById))),
  );
  return useMemo(() => {
    if (elsewhere === null) return null;
    const own = JSON.parse(openKey) as string[];
    return [...elsewhere, ...own.map((orderAction) => ({ template, handle, orderAction }))];
  }, [elsewhere, template, handle, openKey]);
}

/** Cart mode, for a component — the same rule the storefront header runs, over the same list. */
export function useStoreIsInCartMode(): boolean {
  const paymentMethod = useBuilderStore((s) => s.storePaymentMethod);
  const codEnabled = useBuilderStore((s) => s.codConfig.enabled);
  return storeIsInCartMode(useStoreBuyBoxJourneys(), paymentMethod, codEnabled);
}

/** A list the header resolves to "a cart exists" whatever the store's facts — an explicit add-to-cart. */
const CART_ASSUMED: readonly BuyBoxJourney[] = [
  { template: '', handle: '', orderAction: 'add-to-cart' },
];
/** A list the header resolves to "no cart is possible". */
const NO_CART: readonly BuyBoxJourney[] = [];

/**
 * What the CANVAS hands the header as `context.buyBoxJourneys`. The header's rule is `shopperCanHaveCart`
 * over a list; the canvas already holds the rule's ANSWER (`useStoreIsInCartMode`, which is also fail-closed
 * on an unknown method or unknown other pages — two cases the raw list cannot express), so it hands the
 * header a list that yields that same answer. Both values are module constants, so the merged context stays
 * referentially stable and the memoized section cells do not re-render for nothing.
 */
export function canvasBuyBoxJourneys(cartMode: boolean): readonly BuyBoxJourney[] {
  return cartMode ? CART_ASSUMED : NO_CART;
}
